import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  TablePagination, FormControlLabel, Switch, InputAdornment,
} from '@mui/material';
import {
  Sync as SyncIcon, ErrorOutline as ErrorIcon, Search as SearchIcon,
  Visibility as ViewIcon, Link as LinkIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { formatDateTime } from '../formatDate';

const POLL_MS = 3000;


/** Quién sincronizó: nombre, si no email, si no uid (cron = "Cron") */
const actorLabel = (actor) => {
  if (!actor) return '—';
  if (typeof actor === 'string') return actor;
  return actor.name || actor.email || actor.uid || '—';
};

/** Valores de `extra` que valen la pena mostrar como detalle en la tabla de registros */
const extraSummary = (item) => {
  const extra = item.extra || {};
  return Object.entries(extra)
    .filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(',') : String(v)}`);
};

/**
 * Detalle de un catálogo: los registros espejados desde Talana, con
 * búsqueda y paginación en el cliente. Los inactivos (lo que Talana dejó de
 * devolver) están ocultos por defecto; un switch los muestra.
 */
const ErpTalanaCatalogDialog = ({ catalog, label, onClose }) => {
  const { t } = useTranslation();
  const { organization, authToken } = useStore.getState();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        // Siempre todos: el filtro activo/inactivo es en el cliente, así el
        // subtítulo puede mostrar cuántos hay de cada uno
        const response = await api.get(`/erp/talana/catalogs/${catalog.type}`, {
          params: { organizationId: organization, active: 'all' },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (cancelled) return;
        if (response.data.response?.status_response === 'OK') {
          setItems(response.data.response.data || []);
        } else {
          setError(t('erp.talana.detail.loadError'));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(t('erp.talana.detail.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.type, organization]);

  useEffect(() => { setPage(0); }, [search, showInactive]);

  const inactiveCount = items.filter((i) => i.active === false).length;
  const visible = showInactive ? items : items.filter((i) => i.active !== false);
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? visible.filter((i) =>
        String(i.name || '').toLowerCase().includes(needle)
        || String(i.code || '').toLowerCase().includes(needle)
        || String(i.talanaId || '').toLowerCase().includes(needle)
        || extraSummary(i).join(' ').toLowerCase().includes(needle))
    : visible;
  const pageItems = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          {label}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {catalog.collection} · {t('erp.talana.detail.count', { count: filtered.length })}
            {inactiveCount > 0 && ` · ${t('erp.talana.detail.inactiveCount', { count: inactiveCount })}`}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label={<Typography variant="body2">{t('erp.talana.detail.showInactive')}</Typography>}
        />
      </DialogTitle>
      <DialogContent sx={{ pt: '4px !important' }}>
        <TextField
          fullWidth size="small" autoFocus
          placeholder={t('erp.talana.detail.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : filtered.length === 0 ? (
          <Alert severity="info">{t('erp.talana.detail.empty')}</Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('erp.talana.detail.table.talanaId')}</TableCell>
                    <TableCell>{t('erp.talana.detail.table.name')}</TableCell>
                    <TableCell>{t('erp.talana.detail.table.code')}</TableCell>
                    <TableCell>{t('erp.talana.detail.table.details')}</TableCell>
                    {showInactive && <TableCell>{t('erp.common.status')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageItems.map((item) => (
                    <TableRow key={item.uid} hover sx={item.active === false ? { opacity: 0.6 } : undefined}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{String(item.talanaId)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {item.name || '—'}
                        {item.parent !== null && item.parent !== undefined && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('erp.talana.detail.table.parent')}: {String(item.parent)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.code || '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {extraSummary(item).map((chip) => (
                            <Chip key={chip} size="small" variant="outlined" label={chip} sx={{ fontSize: 11, height: 22 }} />
                          ))}
                        </Box>
                      </TableCell>
                      {showInactive && (
                        <TableCell>
                          <Chip size="small"
                            label={item.active === false ? t('erp.common.inactive') : t('erp.common.active')}
                            color={item.active === false ? 'default' : 'success'} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('erp.common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Sincronización de catálogos Talana → Amanda (PLAN_INTEGRACION_TALANA.md
 * §4.2). Talana es el maestro: los catálogos se leen por API y se espejan
 * en colecciones erpTalana*. El sync corre en segundo plano en el backend
 * (rate limit de Talana) y esta pestaña hace polling del estado mientras
 * haya alguno corriendo. Clic en una fila → registros del catálogo.
 */
const ErpTalanaSyncTab = () => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [detail, setDetail] = useState(null); // catálogo abierto en el dialog
  const pollRef = useRef(null);

  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await api.get('/erp/talana/sync/status', {
        params: { organizationId: organization },
        ...headers,
      });
      if (response.data.response?.status_response === 'OK') {
        setStatus(response.data.response.data);
      } else {
        setError(t('erp.talana.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.talana.loadError'));
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, authToken]);

  useEffect(() => {
    if (organization) load();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Polling solo mientras algún catálogo esté corriendo
  const anyRunning = Boolean(status?.running) || (status?.catalogs || []).some((c) => c.running);
  useEffect(() => {
    clearInterval(pollRef.current);
    if (anyRunning) pollRef.current = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [anyRunning, load]);

  // Cruce clientes ↔ centros de costo Talana (PLAN_INTEGRACION_TALANA §4.3):
  // vincula por RUT auxiliar / nombre y crea los que faltan. Sincrónico.
  const reconcileClients = async () => {
    try {
      setReconciling(true);
      const response = await api.post('/erp/clients/talana/reconcile', { organizationId: organization }, headers);
      const body = response.data.response;
      if (body?.status_response === 'OK') {
        const summary = body.data || {};
        showAlert(t('erp.clients.talana.reconcileDone', {
          total: summary.total ?? 0, linked: summary.linked ?? 0, created: summary.created ?? 0, failed: summary.failed ?? 0,
        }), (summary.failed ?? 0) > 0 ? 'warning' : 'success');
        load(true);
      } else {
        showAlert(t('erp.clients.talana.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.status;
      if (code === 503) showAlert(t('erp.talana.notConfigured'), 'warning');
      else showAlert(t('erp.clients.talana.error'), 'error');
    } finally {
      setReconciling(false);
    }
  };

  const startSync = async (catalogs = null) => {
    try {
      setStarting(true);
      const payload = { organizationId: organization };
      if (catalogs) payload.catalogs = catalogs;
      const response = await api.post('/erp/talana/sync', payload, headers);
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.talana.started'), 'success');
        load(true);
      } else {
        showAlert(t('erp.talana.startError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.status;
      if (code === 503) showAlert(t('erp.talana.notConfigured'), 'warning');
      else if (code === 409) showAlert(t('erp.talana.alreadyRunning'), 'info');
      else showAlert(t('erp.talana.startError'), 'error');
    } finally {
      setStarting(false);
    }
  };

  const stateChip = (catalog) => {
    if (catalog.running) return <Chip size="small" color="info" label={t('erp.talana.state.running')} icon={<CircularProgress size={12} color="inherit" />} />;
    if (catalog.state === 'error') return <Chip size="small" color="error" label={t('erp.talana.state.error')} />;
    if (catalog.state === 'ok') return <Chip size="small" color="success" label={t('erp.talana.state.ok')} />;
    return <Chip size="small" label={t('erp.talana.state.never')} />;
  };

  const catalogLabel = (type, fallback) => {
    const key = `erp.talana.catalogs.${type}`;
    const label = t(key);
    return label === key ? fallback : label;
  };

  const canOpen = (catalog) => catalog.state === 'ok' || catalog.state === 'error';
  const busy = starting || anyRunning || status?.configured === false;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="h6" fontWeight={600}>{t('erp.talana.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={t('erp.clients.talana.reconcileHint')}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={reconciling ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                disabled={busy || reconciling}
                onClick={reconcileClients}
                sx={{ textTransform: 'none' }}
              >
                {t('erp.clients.talana.reconcile')}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<SyncIcon />}
            disabled={busy || reconciling}
            onClick={() => startSync()}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
          >
            {anyRunning ? t('erp.talana.syncing') : t('erp.talana.syncAll')}
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('erp.talana.subtitle')}
        {status?.environment && (
          <Chip size="small" variant="outlined" sx={{ ml: 1 }}
            color={status.environment === 'production' ? 'success' : 'warning'}
            label={t(`erp.talana.environment.${status.environment}`)} />
        )}
      </Typography>

      {status?.configured === false && <Alert severity="warning" sx={{ mb: 2 }}>{t('erp.talana.notConfigured')}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.talana.table.catalog')}</TableCell>
                <TableCell>{t('erp.talana.table.state')}</TableCell>
                <TableCell align="right">{t('erp.talana.table.records')}</TableCell>
                <TableCell align="right">{t('erp.talana.table.changes')}</TableCell>
                <TableCell>{t('erp.talana.table.lastSync')}</TableCell>
                <TableCell>{t('erp.talana.table.syncedBy')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(status?.catalogs || []).map((catalog) => (
                <TableRow
                  key={catalog.type}
                  hover
                  onClick={() => canOpen(catalog) && setDetail(catalog)}
                  sx={{ cursor: canOpen(catalog) ? 'pointer' : 'default' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{catalogLabel(catalog.type, catalog.label)}</Typography>
                    <Typography variant="caption" color="text.secondary">{catalog.collection}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stateChip(catalog)}
                      {catalog.lastError && (
                        <Tooltip title={`${catalog.lastError.message || ''}${catalog.lastError.status ? ` (${catalog.lastError.status})` : ''}`}>
                          <ErrorIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{catalog.fetched ?? '—'}</TableCell>
                  <TableCell align="right">
                    {catalog.fetched === null || catalog.fetched === undefined ? '—' : (
                      <Tooltip title={`${t('erp.talana.table.created')}: ${catalog.created} · ${t('erp.talana.table.updated')}: ${catalog.updated}${catalog.deactivated ? ` · ${t('erp.talana.table.deactivated')}: ${catalog.deactivated}` : ''}`}>
                        <span>+{catalog.created} / ~{catalog.updated}</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(catalog.lastSuccessAt)}</TableCell>
                  <TableCell>
                    <Tooltip title={catalog.syncedBy?.email || ''}>
                      <span>{actorLabel(catalog.running ? catalog.startedBy : catalog.syncedBy)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={t('erp.talana.detail.open')}>
                      <span>
                        <IconButton size="small" disabled={!canOpen(catalog)} onClick={() => setDetail(catalog)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('erp.talana.syncOne')}>
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => startSync([catalog.type])}>
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {detail && (
        <ErpTalanaCatalogDialog
          catalog={detail}
          label={catalogLabel(detail.type, detail.label)}
          onClose={() => setDetail(null)}
        />
      )}
    </Box>
  );
};

export default ErpTalanaSyncTab;
